import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import { WarningSign } from "common/Icons/Icons";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import UnorderedListOutlined from "@ant-design/icons/UnorderedListOutlined";
import { CircleCheck, ListChecks } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import React from "react";
import Flex from "common/Boxes/Flex";
import Card from "common/Boxes/Card";
import { typography } from "@shared/common/Themes/typography";
import {
  ConfirmButton,
  IconButton,
} from "features/tradingTerminal/common/styles";
import SummaryModal from "features/tradingTerminal/Summary/SummaryModal";
import { openMessage } from "utils/misc";
import useSummary from "@shared/features/tradingTerminal/common/hooks/useSummary";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface ConfirmAndShowErrorsProps {
  inline?: boolean;
}

const ConfirmAndShowErrors = ({ inline }: ConfirmAndShowErrorsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const theme = useTheme();

  const {
    onActionButtonClick,
    actionButtonDisabled,
    ordersSummaryModalOpen,
    isConfirm,
    onIconButtonClick,
    closeSummaryModal,
    numErrors,
    numErrorsMessage,
  } = useSummary(openMessage);

  return (
    <>
      <Wrapper $inline={!!inline}>
        <Flex style={{ marginRight: 20 }}>
          <Flex style={{ fontSize: typography.fontSizes.XXL, marginRight: 8 }}>
            {numErrors > 0 ? (
              <WarningSign fillColor={theme.colors.orange} />
            ) : (
              <LookIcon
                classic={
                  <CheckOutlined
                    style={{
                      color: theme.colors.primaryBlue,
                      fontSize: typography.fontSizes.XL,
                    }}
                  />
                }
                newLook={
                  <CircleCheck
                    size={20}
                    strokeWidth={NEW_LOOK_ICON_STROKE}
                    color={theme.colors.green}
                  />
                }
              />
            )}
          </Flex>
          <Text type="secondary">{numErrorsMessage}</Text>
        </Flex>
        <Flex>
          <Tooltip title={t("tooltips.trade_summary")}>
            <IconButton onClick={onIconButtonClick} style={{ marginRight: 8 }}>
              <LookIcon
                classic={<UnorderedListOutlined />}
                newLook={
                  <ListChecks size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
                }
              />
            </IconButton>
          </Tooltip>
          <ConfirmButton
            onClick={onActionButtonClick}
            $disabled={actionButtonDisabled}
          >
            {t("buttons.create_trade")}
          </ConfirmButton>
        </Flex>
      </Wrapper>
      <SummaryModal
        visible={ordersSummaryModalOpen}
        closeModal={closeSummaryModal}
        isConfirm={isConfirm}
      />
    </>
  );
};

export default ConfirmAndShowErrors;

const Wrapper = styled(Card)<{ $inline: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  ${(p) =>
    p.$inline
      ? `
    box-shadow: none;
    padding: 8px 4px 8px 12px;
  `
      : `
  background-color: ${p.theme.colors.cardBgColor};
  box-shadow: rgba(0, 0, 0, 0.1) 0 10px 15px -3px,
    rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
  border: ${p.theme.borders.lightest};
  position: fixed;
  left: 50%;
  transform: translate(-50%, 0);
  bottom: 30px;
  width: 500px;
  z-index: 3;
  `}
  ${(p) =>
    p.theme.isNewLook &&
    !p.$inline &&
    `
    border-radius: ${p.theme.radii.card}px;
    padding: 10px 10px 10px 20px;
    border: ${p.theme.borders.panel};
    box-shadow: ${p.theme.shadows.hoverCard};
  `}
`;
